import DropDownBox from "devextreme-react/drop-down-box";
import TreeView from "devextreme-react/tree-view";
import React, { useMemo, useState } from "react";

// Flat tree node consumed by TreeSelect. Each node has a parentId; a node is selectable
// (clickable to commit a selection) if it has a targetKey. Modules/group rows omit it.
// `disabled` keeps a node visible (so the hierarchy reads correctly) but unclickable.
// `disabledReason` is rendered inline after the text to explain why.
// `badge` renders a small pill next to the text (e.g. a type/category label).
export type TreeSelectNode = {
    id: string;
    parentId: string | null;
    text: string;
    targetKey?: string;
    disabled?: boolean;
    disabledReason?: string;
    badge?: string;
    expanded?: boolean;
};

type Props = {
    nodes: TreeSelectNode[];
    value: string;
    selectedLabel: string;
    onChange: (key: string) => void;
    placeholder?: string;
    width?: number | string;
    dropdownWidth?: number;
    dropdownHeight?: number;
    treeHeight?: number;
    disabled?: boolean;
};

// A DevExtreme DropDownBox whose dropdown panel is a searchable hierarchical TreeView.
// The input shows `selectedLabel` for the current `value`. Clicking a node with a targetKey
// commits the change and closes the panel; clicking a group row only expands/collapses.
export default function TreeSelect({
    nodes,
    value,
    selectedLabel,
    onChange,
    placeholder = "Select...",
    width = 360,
    dropdownWidth = 600,
    dropdownHeight = 400,
    treeHeight = 350,
    disabled = false
}: Props) {
    const [opened, setOpened] = useState(false);

    // DropDownBox needs a dataSource/value/displayExpr to render the selected text in its input.
    // A single synthetic item keyed on the current value is enough — full lookups happen in the tree.
    const inputDataSource = useMemo(
        () => (value ? [{ key: value, label: selectedLabel }] : []),
        [value, selectedLabel]
    );

    const contentRender = () => (
        <TreeView
            // TreeView's dataSource type expects dxTreeViewItem; with plain structure + keyExpr/parentIdExpr
            // we feed it our own shape, so a cast is needed here.
            dataSource={nodes as unknown as Record<string, unknown>[]}
            keyExpr="id"
            parentIdExpr="parentId"
            displayExpr="text"
            dataStructure="plain"
            searchEnabled
            searchMode="contains"
            searchExpr="text"
            expandNodesRecursive={false}
            height={treeHeight}
            itemRender={(node: TreeSelectNode) => (
                <span style={node.disabled ? { cursor: "default" } : undefined}>
                    {node.text}
                    {node.badge && (
                        <span style={{
                            marginLeft: "6px",
                            padding: "1px 6px",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "8px",
                            fontSize: "10px",
                            fontWeight: 500,
                            color: "#555"
                        }}>
                            {node.badge}
                        </span>
                    )}
                    {node.disabled && node.disabledReason && ` - ${node.disabledReason}`}
                </span>
            )}
            onItemClick={(e) => {
                const node = e.itemData as TreeSelectNode | undefined;
                if (node?.targetKey && !node.disabled) {
                    onChange(node.targetKey);
                    setOpened(false);
                }
            }}
        />
    );

    return (
        <DropDownBox
            value={value || null}
            disabled={disabled}
            opened={opened}
            onOptionChanged={(e) => {
                if (e.name === "opened") setOpened(!!e.value);
            }}
            dataSource={inputDataSource}
            valueExpr="key"
            displayExpr="label"
            placeholder={placeholder}
            width={width}
            dropDownOptions={{ width: dropdownWidth, height: dropdownHeight }}
            contentRender={contentRender}
        />
    );
}
